import React, { useState, useEffect } from 'react';
import { db, storage, auth, functions } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile, updateEmail, updatePassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { AUTHOR_ACCENT_COLORS, getAccentClasses } from '../../lib/themaCodes';
import {
  Palette, User, CreditCard, Bell, Upload, Save, Check, Globe, Instagram, BookOpen, Users, ShoppingCart, Eye
} from 'lucide-react';

const TABS = [
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'account', label: 'Account', icon: User },
  { id: 'guest-access', label: 'Guest Access', icon: Users },
  { id: 'subscription', label: 'Subscription', icon: CreditCard },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

export default function AuthorSettingsTab() {
  const { authorId, user, isReader: hasReaderRole, forceRefreshClaims } = useAuth();
  const { authorProfile, branding, subscription, basePath } = useAuthorDashboard();
  const [activeTab, setActiveTab] = useState('branding');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [readerToggleLoading, setReaderToggleLoading] = useState(false);

  // Branding form
  const [displayName, setDisplayName] = useState('');
  const [accentColor, setAccentColor] = useState('teal');
  const [bio, setBio] = useState('');
  const [website, setWebsite] = useState('');
  const [socialLinks, setSocialLinks] = useState({ instagram: '', tiktok: '', twitter: '', facebook: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');

  // Account form
  const [accountName, setAccountName] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Guest access settings
  const [guestAccess, setGuestAccess] = useState({
    ebooks: true,
    audiobooks: true,
    physical: true,
  });
  const [guestAccessLoaded, setGuestAccessLoaded] = useState(false);

  // Load current settings
  useEffect(() => {
    if (branding) {
      setDisplayName(branding.displayName || '');
      setAccentColor(branding.accentColor || 'teal');
      setBio(branding.bio || '');
      setWebsite(branding.website || '');
      setSocialLinks(branding.socialLinks || { instagram: '', tiktok: '', twitter: '', facebook: '' });
      setLogoPreview(branding.logo || '');
      setBannerPreview(branding.banner || '');
    }
    if (authorProfile) {
      setAccountName(authorProfile.name || '');
      setAccountEmail(authorProfile.email || '');
    }
  }, [branding, authorProfile]);

  // Load guest access settings
  useEffect(() => {
    if (!authorId) return;
    getDoc(doc(db, 'authors', authorId, 'settings', 'guestAccess')).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setGuestAccess({
          ebooks: data.ebooks ?? true,
          audiobooks: data.audiobooks ?? true,
          physical: data.physical ?? true,
        });
      }
      setGuestAccessLoaded(true);
    }).catch(() => setGuestAccessLoaded(true));
  }, [authorId]);

  async function saveGuestAccess() {
    setIsSaving(true);
    setSaveMessage('');
    try {
      await setDoc(doc(db, 'authors', authorId, 'settings', 'guestAccess'), {
        ebooks: guestAccess.ebooks,
        audiobooks: guestAccess.audiobooks,
        physical: guestAccess.physical,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSaveMessage('Guest access settings saved!');
    } catch (err) {
      setSaveMessage(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }

  function handleFileUpload(file, setFile, setPreview) {
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);
  }

  async function saveBranding() {
    setIsSaving(true);
    setSaveMessage('');
    try {
      let logoUrl = branding?.logo || '';
      let bannerUrl = branding?.banner || '';

      if (logoFile) {
        const fileRef = storageRef(storage, `authors/${authorId}/logo`);
        await uploadBytes(fileRef, logoFile);
        logoUrl = await getDownloadURL(fileRef);
      }
      if (bannerFile) {
        const fileRef = storageRef(storage, `authors/${authorId}/banner`);
        await uploadBytes(fileRef, bannerFile);
        bannerUrl = await getDownloadURL(fileRef);
      }

      await setDoc(doc(db, 'authors', authorId, 'settings', 'branding'), {
        displayName: displayName.trim(),
        accentColor,
        bio: bio.trim(),
        website: website.trim(),
        socialLinks,
        logo: logoUrl,
        banner: bannerUrl,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setSaveMessage('Branding saved!');
      setLogoFile(null);
      setBannerFile(null);
    } catch (err) {
      setSaveMessage(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }

  async function saveAccount() {
    setIsSaving(true);
    setSaveMessage('');
    try {
      // Update Firestore profile
      await updateDoc(doc(db, 'authors', authorId), {
        name: accountName.trim(),
        email: accountEmail.trim(),
        updatedAt: serverTimestamp(),
      });

      // Update Firebase Auth profile
      if (user) {
        await updateProfile(user, { displayName: accountName.trim() });
        if (accountEmail !== user.email) {
          await updateEmail(user, accountEmail);
        }
        if (newPassword) {
          await updatePassword(user, newPassword);
          setNewPassword('');
        }
      }

      setSaveMessage('Account updated!');
    } catch (err) {
      setSaveMessage(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }

  const accent = getAccentClasses(accentColor);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Settings</h1>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSaveMessage(''); }}
              className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {saveMessage && (
        <div className={`rounded-lg p-3 text-sm ${
          saveMessage.startsWith('Error') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
        }`}>
          {saveMessage}
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (() => {
        const previewName = displayName.trim() || 'Your Name';
        const previewInitial = (displayName || authorProfile?.name || 'A').charAt(0).toUpperCase();

        return (
        <div className="flex flex-col-reverse lg:flex-row gap-6">
          {/* Left: Form */}
          <Card className="border-slate-200 flex-1">
            <CardContent className="space-y-4 p-6">
              <div>
                <Label>Display Name</Label>
                <Input value={displayName} onChange={e => setDisplayName(e.target.value)}
                  placeholder="Shown to readers on your portal" />
              </div>

              <div>
                <Label>Logo</Label>
                <div className="mt-1 flex items-center gap-3">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="h-12 w-12 rounded-lg object-cover" />
                  ) : (
                    <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${accent.bg} text-white font-bold text-lg`}>
                      {previewInitial}
                    </div>
                  )}
                  <label className="cursor-pointer rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                    <Upload className="mr-1 inline h-3 w-3" /> Upload
                    <input type="file" accept="image/*" className="hidden"
                      onChange={e => handleFileUpload(e.target.files[0], setLogoFile, setLogoPreview)} />
                  </label>
                </div>
              </div>

              <div>
                <Label>Banner Image</Label>
                <label className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-4 hover:border-slate-300">
                  {bannerPreview ? (
                    <img src={bannerPreview} alt="Banner" className="h-20 w-full rounded object-cover" />
                  ) : (
                    <span className="text-xs text-slate-500"><Upload className="mr-1 inline h-4 w-4" /> Upload banner (1200x300 recommended)</span>
                  )}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={e => handleFileUpload(e.target.files[0], setBannerFile, setBannerPreview)} />
                </label>
              </div>

              <div>
                <Label>Accent Colour</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AUTHOR_ACCENT_COLORS.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setAccentColor(c.id)}
                      className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all ${
                        accentColor === c.id ? 'border-slate-900 scale-110' : 'border-transparent'
                      }`}
                      title={c.label}
                    >
                      <span className={`h-7 w-7 rounded-full bg-${c.tailwind}-600`} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Author Bio</Label>
                <Textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell readers about yourself..." rows={3} />
              </div>

              <div>
                <Label>Website</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-9" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Instagram</Label>
                  <Input value={socialLinks.instagram} onChange={e => setSocialLinks(p => ({ ...p, instagram: e.target.value }))} placeholder="@handle" />
                </div>
                <div>
                  <Label>TikTok</Label>
                  <Input value={socialLinks.tiktok} onChange={e => setSocialLinks(p => ({ ...p, tiktok: e.target.value }))} placeholder="@handle" />
                </div>
                <div>
                  <Label>Twitter / X</Label>
                  <Input value={socialLinks.twitter} onChange={e => setSocialLinks(p => ({ ...p, twitter: e.target.value }))} placeholder="@handle" />
                </div>
                <div>
                  <Label>Facebook</Label>
                  <Input value={socialLinks.facebook} onChange={e => setSocialLinks(p => ({ ...p, facebook: e.target.value }))} placeholder="Page URL" />
                </div>
              </div>

              <Button className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white" onClick={saveBranding} disabled={isSaving}>
                <Save className="h-4 w-4" /> {isSaving ? 'Saving...' : 'Save Branding'}
              </Button>
            </CardContent>
          </Card>

          {/* Right: Live Preview */}
          <div className="lg:w-[380px] shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-4 w-4 text-slate-400" />
              <p className="text-sm font-medium text-slate-600">Preview — how readers will see your page</p>
            </div>
            <Card className="border-slate-200 overflow-hidden">
              {/* Banner */}
              <div className="relative h-28">
                {bannerPreview ? (
                  <img src={bannerPreview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className={`h-full w-full bg-gradient-to-br ${accent.light} via-white to-slate-100`} />
                )}
              </div>

              {/* Author info overlay */}
              <div className="px-4">
                <div className="relative -mt-8 flex items-end gap-3 pb-3">
                  {logoPreview ? (
                    <img src={logoPreview} alt="" className="h-16 w-16 rounded-xl border-[3px] border-white object-cover shadow-md" />
                  ) : (
                    <div className={`flex h-16 w-16 items-center justify-center rounded-xl border-[3px] border-white ${accent.bg} text-xl font-bold text-white shadow-md`}>
                      {previewInitial}
                    </div>
                  )}
                  <div className="pb-0.5 min-w-0">
                    <p className="text-lg font-bold text-slate-900 truncate">{previewName}</p>
                    {bio.trim() && (
                      <p className="text-xs text-slate-500 line-clamp-2">{bio}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Sample store content */}
              <div className="px-4 pb-4 space-y-3">
                <div className={`rounded-lg ${accent.light} ${accent.border} border px-3 py-2 text-xs text-slate-600`}>
                  Want early access to books and to join {previewName}'s ARC team?{' '}
                  <span className={`font-semibold ${accent.text}`}>Register on the reader portal</span>
                </div>

                {/* Sample book card */}
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex gap-3">
                    <div className="h-16 w-11 shrink-0 rounded bg-slate-200" />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="h-3 w-3/4 rounded bg-slate-200" />
                      <div className="h-2 w-1/2 rounded bg-slate-100" />
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs font-bold text-slate-400">£9.99</span>
                        <button className={`rounded-lg ${accent.bg} px-3 py-1 text-[10px] font-semibold text-white`}>
                          <ShoppingCart className="mr-1 inline h-3 w-3" /> Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            <p className="mt-1.5 text-center text-[11px] text-slate-400">Readers will see this on your store</p>
          </div>
        </div>
        );
      })()}

      {/* Account Tab */}
      {activeTab === 'account' && (
        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardContent className="space-y-4 p-6">
              <div>
                <Label>Name</Label>
                <Input value={accountName} onChange={e => setAccountName(e.target.value)} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={accountEmail} onChange={e => setAccountEmail(e.target.value)} />
              </div>
              <div>
                <Label>New Password (leave blank to keep current)</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" />
              </div>
              <Button className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white" onClick={saveAccount} disabled={isSaving}>
                <Save className="h-4 w-4" /> {isSaving ? 'Saving...' : 'Save Account'}
              </Button>
            </CardContent>
          </Card>

          {/* Reader Access */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">Reader access</h3>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {hasReaderRole
                      ? 'You can browse and read on the Reader Portal with this account.'
                      : 'Activate to browse ARCs and read on Narratoria with this same account.'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    if (hasReaderRole) return; // Already enabled, no disable action
                    setReaderToggleLoading(true);
                    setSaveMessage('');
                    try {
                      const setClaimsFn = httpsCallable(functions, 'setCustomClaims');
                      await setClaimsFn({ uid: user.uid, role: 'reader' });
                      await forceRefreshClaims();
                      setSaveMessage('Reader access activated!');
                    } catch (err) {
                      setSaveMessage(`Error: ${err.message}`);
                    } finally {
                      setReaderToggleLoading(false);
                    }
                  }}
                  disabled={readerToggleLoading || hasReaderRole}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    hasReaderRole ? 'bg-teal-600' : 'bg-slate-200'
                  } ${readerToggleLoading ? 'opacity-50' : ''} ${hasReaderRole ? 'cursor-default' : ''}`}
                >
                  <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    hasReaderRole ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
              {hasReaderRole && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50"
                  onClick={() => { window.location.href = '/reader'; }}
                >
                  <BookOpen className="h-4 w-4" /> Go to Reader Portal
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Guest Access Tab */}
      {activeTab === 'guest-access' && (
        <Card className="border-slate-200">
          <CardContent className="space-y-6 p-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Guest Access Controls</h3>
              <p className="text-sm text-slate-500">
                Choose what guest visitors can browse and purchase without a full portal account.
                Guests can buy via Stripe but cannot access ARC programmes, community features, or signed/special editions.
              </p>
            </div>

            {!guestAccessLoaded ? (
              <div className="py-6 text-center text-sm text-slate-400">Loading settings...</div>
            ) : (
              <>
                {[
                  { key: 'ebooks', label: 'eBooks', desc: 'Allow guests to browse and purchase eBooks' },
                  { key: 'audiobooks', label: 'Audiobooks', desc: 'Allow guests to browse and purchase audiobooks' },
                  { key: 'physical', label: 'Physical Books (Amazon Link)', desc: 'Show an Amazon link for physical books. When off, physical books are hidden from guests entirely' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
                    <div>
                      <h4 className="font-medium text-slate-900">{label}</h4>
                      <p className="mt-0.5 text-sm text-slate-500">{desc}</p>
                    </div>
                    <button
                      onClick={() => setGuestAccess(prev => ({ ...prev, [key]: !prev[key] }))}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                        guestAccess[key] ? 'bg-teal-600' : 'bg-slate-200'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        guestAccess[key] ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>
                ))}

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <h4 className="font-medium text-slate-800 mb-1">Per-product overrides</h4>
                  <p className="text-sm text-slate-500">
                    You can override these defaults for individual products. Go to the Orders tab, edit a product, and toggle "Guest access" on or off for that specific item.
                  </p>
                </div>

                <Button className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white" onClick={saveGuestAccess} disabled={isSaving}>
                  <Save className="h-4 w-4" /> {isSaving ? 'Saving...' : 'Save Guest Access'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <Badge className="bg-slate-100 text-slate-700 border-0 text-sm px-3 py-1">
                {subscription?.tier === 'pro' ? 'Pro' : subscription?.tier === 'publisher' ? 'Publisher' : 'Free'}
              </Badge>
              <span className="text-sm text-slate-500">Current plan</span>
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <p>Book limit: <strong>{subscription?.bookLimit || 5}</strong> active books</p>
              <p>Features: {subscription?.features?.length > 0 ? subscription.features.join(', ') : 'Standard'}</p>
            </div>
            <div className="mt-6 rounded-lg border border-teal-200 bg-teal-50 p-4">
              <h3 className="font-medium text-teal-800">Upgrade Coming Soon</h3>
              <p className="mt-1 text-sm text-teal-700">
                Pro and Publisher tiers with increased book limits, publishing checklist access,
                and advanced analytics are coming soon.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Notification preferences coming soon.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
